;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { SellerStatsOutput } from '@qogita/canary-client'
import { CanaryClient } from '@qogita/deprecated-canary-client'
import { queryOptions } from '@tanstack/react-query'
import { getFormattedCurrencyWithSymbol } from 'src/deprecated/utils/getFormattedCurrency'
import { getFormattedNumber } from 'src/deprecated/utils/getFormattedNumber'
import { getPrivateQueryKey } from 'src/deprecated/utils/queryKey'
import { z } from 'zod'

type RateStatus = 'ABOVE_TARGET' | 'ON_TARGET' | 'BELOW_TARGET'

const transformPercentage = (value: string | null, precision: number = 0) => {
  if (value === null) {
    return null
  }

  const numericValue = Number(value) * 100
  return value ? `${numericValue.toFixed(precision)}%` : null
}

const transformInteger = (value: number | null) => {
  return getFormattedNumber(value ?? 0, 0)
}

const transformFloat = (value: string | null) => {
  return getFormattedNumber(value ?? 0, 0)
}

const transformMoney = (value: number | null) => {
  const numericValue = Number(value) || 0 // ensure NaN is converted to 0
  return getFormattedCurrencyWithSymbol(numericValue, 'EUR', 0)
}

/**
 * Zod schema to validate backend response
 */
const schema = z.object({
  fulfilmentRate30daysPercentage: z.string().nullable(),
  fulfilmentRate30daysOver90FulfilledCount: z.number().nullable(),
  fulfilmentRate30daysOver90FulfilledPercentage: z.string().nullable(),
  fulfilmentRate30daysPartiallyFulfilledCount: z.number().nullable(),
  declinedSale30daysCount: z.number().nullable(),
  expiredSale30daysCount: z.number().nullable(),
  allocatedSale30daysCount: z.number().nullable(),
  completedSale30daysCount: z.number().nullable(),
  completedSaleLifetimeCount: z.number().nullable(),
  fulfilmentTimeTotalInDays30daysAverage: z.string().nullable(),
  fulfilmentTimeToShipInDays30daysAverage: z.string().nullable(),
  fulfilmentTimeInTransit30daysAverage: z.string().nullable(),
  claimRate30daysPercentage: z.string().nullable(),
  claim30daysCount: z.number().nullable(),
  sellerInvoicedTotalEuro30days: z.number().nullable(),
  sellerInvoicedTotalEuroLifetime: z.number().nullable(),
  currentShippingVariableCost: z.string().nullable(),
  currentClaimsVariableCost: z.string().nullable(),
  lastMonthValueBasedSellerFaultClaimRate: z.string().nullable(),
  secondLastMonthValueBasedSellerFaultClaimRate: z.string().nullable(),
  stocklistExpiresAt: z.string().nullable().optional(),
})

function getMonthName(monthIndex: number) {
  const date = new Date(2024, monthIndex, 1) // the year/day doesn't metter because we only care about the month
  return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date)
}

/**
 * Zod schema to transform the validated backend response
 * into the format expected by the UI
 */
const transformSchema = schema.transform((data) => {
  const goals = {
    fulfilmentRate: 95,
    poorlyFulfilledRate: 5,
    claimRate: 1,
    fulfilmentTime: 0,
    shippingCostFactor: 5,
    claimCostFactor: 1,
  }

  // Calculate fulfilment rate status
  let fulfilmentRateStatus: RateStatus | null = null
  if (data.fulfilmentRate30daysPercentage !== null) {
    const fulfilmentRate30daysValue = Math.round(
      Number(data.fulfilmentRate30daysPercentage) * 100,
    )
    if (fulfilmentRate30daysValue < goals.fulfilmentRate) {
      fulfilmentRateStatus = 'BELOW_TARGET'
    } else if (fulfilmentRate30daysValue > goals.fulfilmentRate) {
      fulfilmentRateStatus = 'ABOVE_TARGET'
    } else if (fulfilmentRate30daysValue === goals.fulfilmentRate) {
      fulfilmentRateStatus = 'ON_TARGET'
    }
  }

  // Calculate poorly fulfilment rate status
  let poorlyFulfilmentRateStatus: RateStatus | null = null
  if (data.fulfilmentRate30daysOver90FulfilledPercentage !== null) {
    const fulfilmentRate30daysOver90FulfilledValue = Math.round(
      Number(data.fulfilmentRate30daysOver90FulfilledPercentage) * 100,
    )
    if (fulfilmentRate30daysOver90FulfilledValue > goals.poorlyFulfilledRate) {
      poorlyFulfilmentRateStatus = 'BELOW_TARGET'
    } else if (
      fulfilmentRate30daysOver90FulfilledValue < goals.poorlyFulfilledRate
    ) {
      poorlyFulfilmentRateStatus = 'ABOVE_TARGET'
    } else if (
      fulfilmentRate30daysOver90FulfilledValue === goals.poorlyFulfilledRate
    ) {
      poorlyFulfilmentRateStatus = 'ON_TARGET'
    }
  }

  const shippingCostFactorStatus: RateStatus =
    Math.round(Number(data.currentShippingVariableCost) * 100) >
    goals.shippingCostFactor
      ? 'BELOW_TARGET'
      : 'ABOVE_TARGET'

  const claimCostFactorStatus: RateStatus =
    Math.round(Number(data.currentClaimsVariableCost) * 100) >
    goals.claimCostFactor
      ? 'BELOW_TARGET'
      : 'ABOVE_TARGET'

  // Calculate claim rate status
  function getClaimRateStatus(data: string): RateStatus {
    const claimRate = Math.round(Number(data) * 100)
    // The logic is inverted on purpose, the value has to be below the goal to be above the target
    // because the less claims the better
    if (claimRate < goals.claimRate) {
      return 'ABOVE_TARGET'
    } else if (claimRate > goals.claimRate) {
      return 'BELOW_TARGET'
    }
    return 'ON_TARGET'
  }

  const currentMonth = new Date().getMonth()
  const claimRateHistory = [
    {
      month: getMonthName(currentMonth - 2),
      data: data.secondLastMonthValueBasedSellerFaultClaimRate
        ? {
            status: getClaimRateStatus(
              data.secondLastMonthValueBasedSellerFaultClaimRate,
            ),
            value: transformPercentage(
              data.secondLastMonthValueBasedSellerFaultClaimRate,
            ),
          }
        : null,
    },
    {
      month: getMonthName(currentMonth - 1),
      data: data.lastMonthValueBasedSellerFaultClaimRate
        ? {
            status: getClaimRateStatus(
              data.lastMonthValueBasedSellerFaultClaimRate,
            ),
            value: transformPercentage(
              data.lastMonthValueBasedSellerFaultClaimRate,
            ),
          }
        : null,
    },
    {
      month: getMonthName(currentMonth),
      data: null, // We leave current month to TBD because we will never show current month claim rate
    },
  ]

  // Calculate the average fulfilment time status
  let fulfilmentTimeStatus: RateStatus | null = null
  const fulfilmentTimeTotalInDays30daysAverageValue = Math.round(
    Number(data.fulfilmentTimeTotalInDays30daysAverage),
  )
  if (fulfilmentTimeTotalInDays30daysAverageValue < goals.fulfilmentTime) {
    fulfilmentTimeStatus = 'ABOVE_TARGET'
  } else if (
    fulfilmentTimeTotalInDays30daysAverageValue > goals.fulfilmentTime
  ) {
    fulfilmentTimeStatus = 'BELOW_TARGET'
  } else if (
    fulfilmentTimeTotalInDays30daysAverageValue === goals.fulfilmentTime
  ) {
    fulfilmentTimeStatus = 'ON_TARGET'
  }

  const claimRateNumber = Number(data.claimRate30daysPercentage || 0)
  const completedClaim30daysCount = claimRateNumber
    ? (data.claim30daysCount || 0) / claimRateNumber
    : data.completedSale30daysCount

  // Compute UI state
  const hideInsights =
    data.allocatedSale30daysCount === null || data.allocatedSale30daysCount < 10

  return {
    originalData: data,
    // fulfilment rate
    fulfilmentRate30daysPercentage: transformPercentage(
      data.fulfilmentRate30daysPercentage, // 0.989700000000 -> 98%
    ),
    fulfilmentRate30daysOver90FulfilledCount: transformInteger(
      data.fulfilmentRate30daysOver90FulfilledCount, // 1000 -> 1,000
    ),
    fulfilmentRate30daysPartiallyFulfilledCount: transformInteger(
      data.fulfilmentRate30daysPartiallyFulfilledCount,
    ),
    fulfilmentRate30daysOver90FulfilledPercentage: transformPercentage(
      data.fulfilmentRate30daysOver90FulfilledPercentage, // 0.989700000000 -> 98%
    ),
    // sale counts
    declinedSale30daysCount: transformInteger(data.declinedSale30daysCount),
    expiredSale30daysCount: transformInteger(data.expiredSale30daysCount),
    allocatedSale30daysCount: transformInteger(data.allocatedSale30daysCount),
    completedSale30daysCount: transformInteger(data.completedSale30daysCount),
    completedSaleLifetimeCount: transformInteger(
      data.completedSaleLifetimeCount,
    ),
    // fulfilment time
    fulfilmentTimeTotalInDays30daysAverage:
      fulfilmentTimeTotalInDays30daysAverageValue,
    fulfilmentTimeToShipInDays30daysAverage: transformPercentage(
      data.fulfilmentTimeToShipInDays30daysAverage,
    ),
    fulfilmentTimeInTransit30daysAverage: transformFloat(
      data.fulfilmentTimeInTransit30daysAverage,
    ),
    // claim
    claimRateHistory,
    claim30daysCount: transformInteger(data.claim30daysCount),
    completedClaim30daysCount: transformInteger(completedClaim30daysCount),
    // money
    sellerInvoicedTotalEuro30days: transformMoney(
      data.sellerInvoicedTotalEuro30days,
    ),
    sellerInvoicedTotalEuroLifetime: transformMoney(
      data.sellerInvoicedTotalEuroLifetime,
    ),
    // pricing insights
    currentShippingVariableCost: transformPercentage(
      data.currentShippingVariableCost,
      1,
    ),
    currentClaimsVariableCost: transformPercentage(
      data.currentClaimsVariableCost,
      1,
    ),
    // computed statuses
    fulfilmentRateStatus: hideInsights ? null : fulfilmentRateStatus,
    fulfilmentTimeStatus: hideInsights ? null : fulfilmentTimeStatus,
    poorlyFulfilmentRateStatus: hideInsights
      ? null
      : poorlyFulfilmentRateStatus,
    shippingCostFactorStatus,
    claimCostFactorStatus,
    // Ui state
    hideInsights,
    goals,
  }
})

/**
 * Helper function to help identifying unexpected schema changes
 * from the point of view of this zod schema
 */
const zodParseTypeGuard = (data: typeof transformSchema._input) => {
  return transformSchema.parse(data)
}

const emptyData: SellerStatsOutput = {
  fulfilmentRate30daysPercentage: null,
  fulfilmentRate30daysOver90FulfilledPercentage: null,
  fulfilmentRate30daysOver90FulfilledCount: null,
  fulfilmentRate30daysPartiallyFulfilledPercentage: null,
  fulfilmentRate30daysPartiallyFulfilledCount: 0,
  declinedSale30daysCount: 0,
  declinedSale30daysPercentage: null,
  expiredSale30daysCount: 0,
  expiredSale30daysPercentage: null,
  allocatedSale30daysCount: 0,
  completedSale30daysCount: 0,
  completedSaleLifetimeCount: 0,
  fulfilmentTimeTotalInDays30daysAverage: null,
  fulfilmentTimeToShipInDays30daysAverage: null,
  fulfilmentTimeInTransit30daysAverage: null,
  claimRate30daysPercentage: null,
  claim30daysCount: 0,
  sellerInvoicedTotalEuro30days: null,
  sellerInvoicedTotalEuroLifetime: null,
  lastMonthValueBasedSellerFaultClaimRate: null,
  secondLastMonthValueBasedSellerFaultClaimRate: null,
  stocklistExpiresAt: null,
  currentShippingVariableCost: null,
  currentClaimsVariableCost: null,
}

export function getSellerQueries(canaryClient: CanaryClient) {
  const queries = {
    all: () => [...getPrivateQueryKey(), 'sellers'] as const,
    stats: () =>
      queryOptions({
        queryKey: [...queries.all(), 'stats'],
        queryFn: async () => {
          const sellersStats = await canaryClient.getSellersStats()

          return zodParseTypeGuard(sellersStats || emptyData)
        },
      }),
  }

  return queries
}
